/**
 * Transcripción local Whisper (ONNX / Transformers.js) en desktop.
 * Worker en public/voice — no entra al bundle CRA.
 * Modelo (tiny/base/small) se elige on-the-fly vía probeDeviceCapability.
 */
import {
  probeDeviceCapability,
  resolveWhisperModel,
  whisperModelIdForTier,
} from './deviceCapability'
import { resolveLiveModelFromPrefs, getTranscribeDesktopPref } from './sttPrefs'
import {
  applyWorkerProgressToRuntime,
  applyWorkerReadyToRuntime,
  markSttIdle,
  markSttInferring,
  scheduleNpuProbe,
} from './sttRuntime'

const TARGET_SR = 16000
const WORKER_TIMEOUT_MS = {
  tiny: 180000,
  base: 240000,
  small: 360000,
}

/** @type {Worker|null} */
let worker = null
/** @type {Map<string, { resolve: Function, reject: Function, onProgress?: Function, modelTier?: string|null }>} */
const pending = new Map()
let seq = 0
/** Cola serial: una inferencia a la vez (live chunks + full). */
let queueTail = Promise.resolve()

/**
 * @param {() => Promise<any>} job
 * @returns {Promise<any>}
 */
function enqueueJob(job) {
  const run = queueTail.then(job, job)
  queueTail = run.then(
    () => undefined,
    () => undefined
  )
  return run
}

/**
 * @returns {boolean}
 */
export function isLocalWhisperEnabled() {
  return getTranscribeDesktopPref()
}

/**
 * Desktop (no UA móvil) + cores/RAM + pref transcripción.
 * @returns {boolean}
 */
export function canRunLocalWhisper() {
  if (!getTranscribeDesktopPref()) return false
  try {
    return !!probeDeviceCapability().canLocalWhisper
  } catch (e) {
    return false
  }
}

/**
 * Motivo de skip para UI / logs (null si puede correr).
 * @returns {string|null}
 */
export function getLocalWhisperSkipReason() {
  if (!getTranscribeDesktopPref()) return 'desktop_transcribe_off'
  try {
    const p = probeDeviceCapability()
    return p.canLocalWhisper ? null : p.whisperSkipReason || 'unknown'
  } catch (e) {
    return 'error'
  }
}

/**
 * @returns {string}
 */
function resolveWorkerUrl() {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.href) {
      let path = '/voice/whisperTranscribe.worker.js'
      try {
        const base =
          (typeof process !== 'undefined' &&
            process.env &&
            process.env.PUBLIC_URL) ||
          ''
        const prefix = String(base || '').replace(/\/+$/, '')
        path = `${prefix}/voice/whisperTranscribe.worker.js`.replace(/\/{2,}/g, '/')
        if (!path.startsWith('/')) path = `/${path}`
      } catch (e) {
        path = '/voice/whisperTranscribe.worker.js'
      }
      return new URL(path, window.location.href).href
    }
  } catch (e) {
    /* fall through */
  }
  return './voice/whisperTranscribe.worker.js'
}

function ensureWorker() {
  if (worker) return worker
  if (typeof Worker === 'undefined') {
    throw new Error('Web Worker no disponible')
  }
  scheduleNpuProbe()
  const url = resolveWorkerUrl()
  worker = new Worker(url, { type: 'module' })
  worker.onmessage = (ev) => {
    const msg = ev && ev.data
    if (!msg) return
    const id = msg.id
    if (msg.type === 'progress') {
      try {
        applyWorkerProgressToRuntime(msg)
      } catch (e) {
        /* ignore */
      }
      if (id && pending.has(id) && pending.get(id).onProgress) {
        try {
          pending.get(id).onProgress(msg)
        } catch (e) {
          /* ignore */
        }
      }
      return
    }
    if (msg.type === 'ready') {
      try {
        applyWorkerReadyToRuntime(msg)
      } catch (e) {
        /* ignore */
      }
      if (id && pending.has(id)) {
        const slot = pending.get(id)
        pending.delete(id)
        slot.resolve({
          ok: true,
          modelId: msg.modelId || null,
          source: 'local_whisper',
          cached: !!msg.cached,
          modelsLoaded: Array.isArray(msg.modelsLoaded) ? msg.modelsLoaded : [],
          backend: msg.backend || 'wasm',
        })
      }
      return
    }
    if (!id || !pending.has(id)) return
    const slot = pending.get(id)
    pending.delete(id)
    if (msg.type === 'done') {
      try {
        if (Array.isArray(msg.modelsLoaded)) {
          applyWorkerReadyToRuntime(msg)
        }
        markSttIdle()
      } catch (e) {
        /* ignore */
      }
      slot.resolve({
        text: String(msg.text || '').trim(),
        source: 'local_whisper',
        modelId: msg.modelId || null,
        modelTier: slot.modelTier || null,
        words: Array.isArray(msg.words) ? msg.words : [],
        backend: msg.backend || 'wasm',
      })
    } else {
      try {
        markSttIdle()
      } catch (e2) {
        /* ignore */
      }
      slot.reject(new Error(msg.message || 'Error de transcripción'))
    }
  }
  worker.onerror = (err) => {
    const message =
      (err && err.message) || 'Falló el worker de Whisper'
    pending.forEach((slot) => {
      try {
        slot.reject(new Error(message))
      } catch (e) {
        /* ignore */
      }
    })
    pending.clear()
    try {
      worker.terminate()
    } catch (e2) {
      /* ignore */
    }
    worker = null
  }
  return worker
}

/**
 * Decode audio Blob → mono Float32 PCM (sampleRate nativo).
 * @param {Blob} blob
 * @returns {Promise<{ pcm: Float32Array, sampleRate: number }>}
 */
export async function decodeAudioBlobToMonoPcm(blob) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext
  if (!AudioCtx) throw new Error('AudioContext no disponible')
  const ctx = new AudioCtx()
  try {
    const ab = await blob.arrayBuffer()
    const audioBuffer = await new Promise((resolve, reject) => {
      ctx.decodeAudioData(
        ab.slice(0),
        (buf) => resolve(buf),
        (err) => reject(err || new Error('decodeAudioData falló'))
      )
    })
    const sampleRate = audioBuffer.sampleRate
    const len = audioBuffer.length
    const mono = new Float32Array(len)
    const ch0 = audioBuffer.getChannelData(0)
    if (audioBuffer.numberOfChannels === 1) {
      mono.set(ch0)
    } else {
      const ch1 = audioBuffer.getChannelData(1)
      for (let i = 0; i < len; i++) {
        mono[i] = (ch0[i] + ch1[i]) * 0.5
      }
    }
    return { pcm: mono, sampleRate }
  } finally {
    try {
      await ctx.close()
    } catch (e) {
      /* ignore */
    }
  }
}

/**
 * @param {Blob} audioBlob
 * @param {{ onProgress?: (p: object) => void, signal?: AbortSignal }} [opts]
 * @returns {Promise<{
 *   text: string,
 *   source: 'local_whisper',
 *   modelId?: string|null,
 *   modelTier?: string|null,
 *   words?: { word: string, start: number, end: number }[],
 * }|null>}
 *   null si no aplica (móvil / flag off)
 */
export async function transcribeLocal(audioBlob, opts = {}) {
  if (!canRunLocalWhisper()) return null
  if (!audioBlob) throw new Error('Audio vacío')
  if (opts.signal && opts.signal.aborted) {
    const err = new Error('Transcripción cancelada')
    err.name = 'AbortError'
    throw err
  }

  const resolved =
    opts.modelId || opts.modelTier
      ? {
          tier: opts.modelTier || 'tiny',
          modelId:
            opts.modelId ||
            whisperModelIdForTier(opts.modelTier || 'tiny'),
        }
      : resolveWhisperModel()
  const { tier, modelId } = resolved
  const timeoutMs = WORKER_TIMEOUT_MS[tier] || WORKER_TIMEOUT_MS.tiny

  try {
    console.info('[UDV] whisper model', tier, modelId)
  } catch (e) {
    /* ignore */
  }

  const { pcm, sampleRate } = await decodeAudioBlobToMonoPcm(audioBlob)
  return enqueueJob(
    () =>
      new Promise((resolve, reject) => {
        const w = ensureWorker()
        try {
          markSttInferring()
        } catch (e) {
          /* ignore */
        }
        const id = `t-${Date.now()}-${++seq}`
        const timer = setTimeout(() => {
          pending.delete(id)
          reject(new Error('Timeout de transcripción Whisper'))
        }, timeoutMs)

        const onAbort = () => {
          clearTimeout(timer)
          pending.delete(id)
          const err = new Error('Transcripción cancelada')
          err.name = 'AbortError'
          reject(err)
        }
        if (opts.signal) {
          if (opts.signal.aborted) {
            onAbort()
            return
          }
          opts.signal.addEventListener('abort', onAbort, { once: true })
        }

        pending.set(id, {
          modelTier: tier,
          resolve: (v) => {
            clearTimeout(timer)
            if (opts.signal) opts.signal.removeEventListener('abort', onAbort)
            resolve(v)
          },
          reject: (e) => {
            clearTimeout(timer)
            if (opts.signal) opts.signal.removeEventListener('abort', onAbort)
            reject(e)
          },
          onProgress: opts.onProgress,
        })

        try {
          w.postMessage(
            {
              type: 'transcribe',
              id,
              modelId,
              pcm: pcm.buffer,
              sampleRate: sampleRate || TARGET_SR,
            },
            [pcm.buffer]
          )
        } catch (e) {
          clearTimeout(timer)
          pending.delete(id)
          reject(e)
        }
      })
  )
}

/**
 * Precarga el pipeline Whisper (idle).
 * @param {{ modelId?: string, modelTier?: string }} [opts]
 * @returns {Promise<{ ok: boolean, modelId?: string|null }|null>}
 */
export async function preloadWhisper(opts = {}) {
  if (!canRunLocalWhisper()) return null
  const resolved = opts.modelId
    ? { modelId: opts.modelId, tier: opts.modelTier || 'tiny' }
    : resolveWhisperModel()
  const modelId = resolved.modelId
  const tier = resolved.tier || 'tiny'

  return enqueueJob(
    () =>
      new Promise((resolve, reject) => {
        const w = ensureWorker()
        const id = `preload-${Date.now()}-${++seq}`
        const timer = setTimeout(() => {
          pending.delete(id)
          reject(new Error('Timeout preload Whisper'))
        }, WORKER_TIMEOUT_MS[tier] || WORKER_TIMEOUT_MS.tiny)

        pending.set(id, {
          modelTier: tier,
          resolve: (v) => {
            clearTimeout(timer)
            resolve(v)
          },
          reject: (e) => {
            clearTimeout(timer)
            reject(e)
          },
          onProgress: opts.onProgress,
        })

        try {
          w.postMessage({ type: 'preload', id, modelId })
        } catch (e) {
          clearTimeout(timer)
          pending.delete(id)
          reject(e)
        }
      })
  )
}

/**
 * Precarga modelos Whisper (cola serial).
 * Por defecto solo `live` (más liviano). El `final` se carga on-demand al transcribir.
 * @param {{ onProgress?: (p: object) => void, mode?: 'live'|'final'|'all' }} [opts]
 * @returns {Promise<{ ok: boolean, modelsLoaded?: string[] }|null>}
 */
export async function preloadWhisperModels(opts = {}) {
  if (!canRunLocalWhisper()) return null
  const mode = opts.mode === 'final' || opts.mode === 'all' ? opts.mode : 'live'
  const loaded = []

  if (mode === 'live' || mode === 'all') {
    const live = resolveLiveWhisperModel()
    try {
      const r1 = await preloadWhisper({
        modelId: live.modelId,
        modelTier: live.tier,
        onProgress: opts.onProgress,
      })
      if (r1 && r1.modelsLoaded) loaded.push(...r1.modelsLoaded)
      else if (r1 && r1.modelId) loaded.push(r1.modelId)
    } catch (e) {
      try {
        console.warn('[UDV] whisper preload live failed', e)
      } catch (e2) {
        /* ignore */
      }
    }
  }

  if (mode === 'final' || mode === 'all') {
    const final = resolveWhisperModel()
    try {
      const r2 = await preloadWhisper({
        modelId: final.modelId,
        modelTier: final.tier,
        onProgress: opts.onProgress,
      })
      if (r2 && r2.modelsLoaded) {
        r2.modelsLoaded.forEach((m) => {
          if (!loaded.includes(m)) loaded.push(m)
        })
      } else if (r2 && r2.modelId && !loaded.includes(r2.modelId)) {
        loaded.push(r2.modelId)
      }
    } catch (e) {
      try {
        console.warn('[UDV] whisper preload final failed', e)
      } catch (e2) {
        /* ignore */
      }
    }
  }

  return loaded.length ? { ok: true, modelsLoaded: loaded } : null
}

/**
 * Transcribe un segmento corto (live). Cola serial.
 * @param {Float32Array} pcm
 * @param {{
 *   sampleRate?: number,
 *   modelId?: string,
 *   modelTier?: string,
 *   initialPrompt?: string,
 * }} [opts]
 * @returns {Promise<{ text: string, source: 'local_whisper', modelId?: string|null }|null>}
 */
export async function transcribeChunk(pcm, opts = {}) {
  if (!canRunLocalWhisper()) return null
  if (!pcm || !pcm.length) return { text: '', source: 'local_whisper' }

  const resolved = opts.modelId
    ? { modelId: opts.modelId, tier: opts.modelTier || 'tiny' }
    : resolveLiveWhisperModel()
  const modelId = resolved.modelId
  const tier = resolved.tier || 'tiny'
  const timeoutMs = Math.min(
    90000,
    WORKER_TIMEOUT_MS[tier] || WORKER_TIMEOUT_MS.tiny
  )

  const copy = pcm.slice(0)
  return enqueueJob(
    () =>
      new Promise((resolve, reject) => {
        const w = ensureWorker()
        try {
          markSttInferring()
        } catch (e) {
          /* ignore */
        }
        const id = `c-${Date.now()}-${++seq}`
        const timer = setTimeout(() => {
          pending.delete(id)
          reject(new Error('Timeout chunk Whisper'))
        }, timeoutMs)

        pending.set(id, {
          modelTier: tier,
          resolve: (v) => {
            clearTimeout(timer)
            resolve(v)
          },
          reject: (e) => {
            clearTimeout(timer)
            reject(e)
          },
        })

        try {
          w.postMessage(
            {
              type: 'transcribeChunk',
              id,
              modelId,
              sampleRate: opts.sampleRate || TARGET_SR,
              initialPrompt: opts.initialPrompt,
              pcm: copy.buffer,
            },
            [copy.buffer]
          )
        } catch (e) {
          clearTimeout(timer)
          pending.delete(id)
          reject(e)
        }
      })
  )
}

/**
 * Modelo para live streaming (prefs + heurística).
 * @returns {{ tier: string, modelId: string }}
 */
export function resolveLiveWhisperModel() {
  try {
    const r = resolveLiveModelFromPrefs()
    return { tier: r.tier, modelId: r.modelId }
  } catch (e) {
    const full = resolveWhisperModel()
    if (full.tier === 'small') {
      return { tier: 'base', modelId: whisperModelIdForTier('base') }
    }
    return { tier: 'tiny', modelId: whisperModelIdForTier('tiny') }
  }
}

export default transcribeLocal
